import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Search, Check } from "lucide-react";
import { useCreatives } from "../../../../context/CreativeContext";
import { useScreens } from "../../../../context/ScreenContext";
import CreativeSelectionCard from "../../../ui/CreativeSelectionCard";
import { ReactComponent as ScheduledCreative } from "../../../../assets/icons/svg/Scheduled-Creative.svg";
import { ReactComponent as All } from "../../../../assets/icons/svg/all.svg";
import { ReactComponent as Landscape } from "../../../../assets/icons/svg/landscape.svg";
import { ReactComponent as Protrait } from "../../../../assets/icons/svg/Protrait.svg";
import { ReactComponent as Thunder } from "../../../../assets/icons/svg/thunder.svg";
import { ReactComponent as Clock } from "../../../../assets/icons/svg/clock.svg";
import { ReactComponent as SelectionIcon } from "../../../../assets/icons/svg/selection-icon.svg";
import { useTopBar } from "../../../../context/TopBarContext";
import ItemListPopup from "../../../ui/ItemListPopup";
import AnnouncementSelector from "../../../ui/AnnouncementCard";
import CustomButton from "../../../ui/CommonButton";
import NewSelectorCard from "../../../ui/New_Shedule_card";
import LaterSelectorCard from "../../../ui/Later_Shedule_Card";
import WeeklySchedule from "../../../ui/Selaction_Schedule_card";
import { useScheduledCreatives } from "../../../../context/ScheduledCreativesContext";

// Type definitions
type FilterType = "all" | "landscape" | "portrait";
type AnnouncementType = "general" | "tag-level";
type ScheduleType = "now" | "later" | "selection";

// Schedule data interfaces
interface DurationOption {
  value: string;
  label: string;
}

interface NowScheduleData {
  endDate: Date;
  duration: DurationOption | null;
}

interface LaterScheduleData {
  startDate: Date;
  endDate: Date;
  duration: DurationOption | null;
}

interface SelectionScheduleData {
  selectedSlots: Set<string>;
  duration: DurationOption | null;
}

// Enhanced Schedule Data Manager
class ScheduleDataManager {
  private data: Map<ScheduleType, any> = new Map();

  setData(type: ScheduleType, scheduleData: any): void {
    this.data.set(type, scheduleData);
  }

  getData(type: ScheduleType): any {
    return this.data.get(type);
  }

  clearAll(): void {
    this.data.clear();
  }

  // Check if multiple schedule types have complete data
  hasMultipleScheduleTypes(): boolean {
    const completedTypes: ScheduleType[] = [];

    this.data.forEach((data, type) => {
      if (this.isScheduleDataComplete(type, data)) {
        completedTypes.push(type);
      }
    });

    return completedTypes.length > 1;
  }

  // Get the number of completed schedule types
  getCompletedScheduleTypesCount(): number {
    let count = 0;
    this.data.forEach((data, type) => {
      if (this.isScheduleDataComplete(type, data)) {
        count++;
      }
    });
    return count;
  }

  // Get completed schedule types
  getCompletedScheduleTypes(): ScheduleType[] {
    const completedTypes: ScheduleType[] = [];
    this.data.forEach((data, type) => {
      if (this.isScheduleDataComplete(type, data)) {
        completedTypes.push(type);
      }
    });
    return completedTypes;
  }

  // Clear other schedule types when one is being actively configured
  clearOtherScheduleTypes(keepType: ScheduleType): void {
    const dataToKeep = this.data.get(keepType);
    this.data.clear();
    if (dataToKeep) {
      this.data.set(keepType, dataToKeep);
    }
  }

  // Validation logic
  isScheduleDataComplete(type: ScheduleType, data: any): boolean {
    if (!data) return false;

    switch (type) {
      case "now":
        return !!(data.endDate && data.duration?.value);
      case "later":
        return !!(data.startDate && data.endDate && data.duration?.value);
      case "selection":
        return !!(data.selectedSlots?.size > 0 && data.duration?.value);
      default:
        return false;
    }
  }
}

interface ScheduledCreative {
  id: string;
  title: string;
  imageUrl: string;
  orientation: "landscape" | "portrait";
  format: string;
  createdOn: string;
  dateRange: string;
  locations: string;
  tags: string[];
  isPlaying: boolean;
  scheduleType: ScheduleType;
  announcement: "general" | "tag-level";
  scheduleDetails: any;
}

// Progress Bar Component
const ProgressBar = ({ currentStep }: { currentStep: number }) => {
  return (
    <div className="flex items-center gap-4 mb-6">
      <div className="flex items-center gap-2">
        <div
          className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
            currentStep > 1
              ? "bg-[#0091AF] text-white"
              : currentStep === 1
              ? "bg-black text-white"
              : "border border-gray-300 text-gray-500"
          }`}
        >
          {currentStep > 1 ? <Check className="w-3 h-3" /> : "1"}
        </div>
        <span className="text-sm font-medium text-gray-700">Where</span>
      </div>
      <div className="w-8 h-px bg-gray-300"></div>
      <div className="flex items-center gap-2">
        <div
          className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
            currentStep >= 2
              ? "bg-black text-white"
              : "border border-gray-300 text-gray-500"
          }`}
        >
          2
        </div>
        <span className="text-sm font-medium text-gray-700">When</span>
      </div>
    </div>
  );
};

// Updated Schedule Buttons Component
const ScheduleButtons = ({
  onScheduleSelect,
  selectedSchedule,
  scheduleDataManager,
  onScheduleDataChange,
  onClearOtherSchedules,
}: {
  onScheduleSelect: (type: ScheduleType) => void;
  selectedSchedule: ScheduleType | null;
  scheduleDataManager: ScheduleDataManager;
  onScheduleDataChange: (type: ScheduleType, data: any) => void;
  onClearOtherSchedules: (keepType: ScheduleType) => void;
}) => {
  const getButtonStyle = (type: ScheduleType) => {
    const isActive = selectedSchedule === type;
    const baseBtnStyle =
      "w-full flex items-center justify-center gap-1.5 2xl:gap-2 py-2 2xl:py-3 rounded-[8px] text-xs 2xl:text-sm font-medium text-black transition-all duration-200";
    const hoverStyle = "hover:bg-[#F2FDFF] hover:border-[#0BA6C7]";
    const dynamicBorderColor = isActive
      ? "border-[#0BA6C7]"
      : "border-[#1F1F1F]";
    const activeBgStyle = isActive ? "bg-[#F2FDFF]" : "bg-white";

    return `${baseBtnStyle} border-[0.5px] ${dynamicBorderColor} ${hoverStyle} ${activeBgStyle}`;
  };

  const completedTypes = scheduleDataManager.getCompletedScheduleTypes();
  const hasMultipleCompleted = completedTypes.length > 1;

  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-lg font-semibold text-black mt-1 mb-1">Schedule:</h3>

      <div className="flex gap-3">
        <div className="flex-1">
          <button
            onClick={() => onScheduleSelect("now")}
            className={getButtonStyle("now")}
          >
            <Thunder className="w-5 h-5" />
            <span>Now</span>
          </button>
        </div>
        <div className="flex-1">
          <button
            onClick={() => onScheduleSelect("later")}
            className={getButtonStyle("later")}
          >
            <Clock className="w-5 h-5" />
            <span>Later</span>
          </button>
        </div>
        <div className="flex-1">
          <button
            onClick={() => onScheduleSelect("selection")}
            className={getButtonStyle("selection")}
          >
            <SelectionIcon className="w-5 h-5" />
            <span>Selection</span>
          </button>
        </div>
      </div>

      {/* Show error message when multiple schedule types have data */}
      {hasMultipleCompleted && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3">
          <div className="flex flex-col gap-2">
            <p className="text-sm text-red-600">
              Multiple schedule types are completed: {completedTypes.join(", ")}
            </p>
            <p className="text-xs text-red-500">
              Please choose one schedule type to proceed with.
            </p>
            <div className="flex gap-2 mt-2">
              {completedTypes.map((type) => (
                <button
                  key={type}
                  onClick={() => onClearOtherSchedules(type)}
                  className="px-3 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                >
                  Keep {type.charAt(0).toUpperCase() + type.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Schedule components */}
      {selectedSchedule === "now" && (
        <NewSelectorCard
          initialData={scheduleDataManager.getData("now")}
          onDataChange={(data: NowScheduleData) =>
            onScheduleDataChange("now", data)
          }
        />
      )}
      {selectedSchedule === "later" && (
        <LaterSelectorCard
          initialData={scheduleDataManager.getData("later")}
          onDataChange={(data: LaterScheduleData) =>
            onScheduleDataChange("later", data)
          }
        />
      )}
      {selectedSchedule === "selection" && (
        <WeeklySchedule
          initialData={scheduleDataManager.getData("selection")}
          onDataChange={(data: SelectionScheduleData) =>
            onScheduleDataChange("selection", data)
          }
        />
      )}
    </div>
  );
};

const ScheduleCreativePage: React.FC = () => {
  const navigate = useNavigate();
  const { creatives } = useCreatives();
  const { screens } = useScreens();
  const { addScheduledCreative, scheduledCreatives } = useScheduledCreatives();
  const { setTitle, setShowBackButton, pushNavState } = useTopBar();
  const location = useLocation();

  // State
  const [selectedCreative, setSelectedCreative] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterType>("all");
  const [search, setSearch] = useState("");
  const [selectedAnnouncement, setSelectedAnnouncement] =
    useState<AnnouncementType | null>(null);
  const [isAnnouncementComplete, setIsAnnouncementComplete] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState<ScheduleType | null>(
    "now"
  );
  const [isScheduleComplete, setIsScheduleComplete] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [scheduleDataManager] = useState(() => new ScheduleDataManager());
  const [announcementDetails, setAnnouncementDetails] = useState<any>(null);

  // REMOVED: isCreativeScheduled function and unscheduledCreatives filter
  // Now all creatives are available for scheduling regardless of previous schedules

  // Filter creatives based on search and filter criteria (no schedule validation)
  const filteredCreatives = creatives.filter((creative) => {
    const matchesSearch = creative.title
      ?.toLowerCase()
      .includes(search.toLowerCase());
    const matchesFilter =
      filter === "all" ||
      (filter === "landscape" && creative.orientation === "landscape") ||
      (filter === "portrait" && creative.orientation === "portrait");
    return matchesSearch && matchesFilter;
  });

  // Setup navigation
  useEffect(() => {
    const title = "Schedule creative";
    const showBack = true;
    setTitle(title);
    setShowBackButton(showBack);
    pushNavState({
      path: location.pathname,
      title,
      showBackButton: showBack,
    });
  }, [location.pathname, setTitle, setShowBackButton, pushNavState]);

  // Get matching screens based on creative tags
  const getMatchingScreens = (creativeTags: string[]) => {
    if (!creativeTags?.length || !screens?.length) return [];

    return screens.filter((screen) => {
      if (!screen.tags?.length) return false;
      return screen.tags.some((screenTag) =>
        creativeTags.some(
          (creativeTag) =>
            screenTag.value.toLowerCase() === creativeTag.toLowerCase() ||
            screenTag.tag.toLowerCase() === creativeTag.toLowerCase()
        )
      );
    });
  };

  // Generate locations string
  const generateLocationsString = (creativeTags: string[]) => {
    const matchingScreens = getMatchingScreens(creativeTags);
    if (matchingScreens.length === 0) return "No matching screens found";

    const screenNames = Array.from(
      new Set(matchingScreens.map((screen) => screen.name))
    );
    if (screenNames.length <= 3) {
      return screenNames.join(", ");
    }
    return `${screenNames.slice(0, 2).join(", ")}, +${
      screenNames.length - 2
    } more`;
  };

  // Generate date range string
  const generateDateRange = (
    scheduleType: ScheduleType,
    scheduleDetails: any
  ): string => {
    const formatDate = (date: Date) => {
      return date.toLocaleDateString("en-US", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    };

    switch (scheduleType) {
      case "now":
        return `${formatDate(new Date())} - ${formatDate(
          scheduleDetails.endDate
        )}`;
      case "later":
        return `${formatDate(scheduleDetails.startDate)} - ${formatDate(
          scheduleDetails.endDate
        )}`;
      case "selection":
        return "Custom Schedule";
      default:
        return "Not scheduled";
    }
  };

  // Generate tags
  const generateTags = (
    announcement: AnnouncementType,
    scheduleType: ScheduleType,
    announcementDetails?: any
  ): string[] => {
    const tags = [];

    if (announcement === "general") {
      tags.push("General");
    } else if (announcement === "tag-level" && announcementDetails) {
      if (announcementDetails.selectedTags?.length > 0) {
        announcementDetails.selectedTags.forEach((tagId: string) => {
          const tagName = tagId.charAt(0).toUpperCase() + tagId.slice(1);
          tags.push(tagName);
        });
      }

      if (announcementDetails.selectedValues?.length > 0) {
        announcementDetails.selectedValues.forEach((valueId: string) => {
          const valueLabel =
            announcementDetails.availableValues?.find(
              (option: any) => option.id === valueId
            )?.label || valueId;
          tags.push(valueLabel);
        });
      }
    }

    // Add schedule type tags
    const scheduleTypeTags = {
      now: "Immediate",
      later: "Scheduled",
      selection: "Custom",
    };
    tags.push(scheduleTypeTags[scheduleType]);

    return tags;
  };

  // Check if schedule data is complete
  const isScheduleDataComplete = (type: ScheduleType, data: any): boolean => {
    return scheduleDataManager.isScheduleDataComplete(type, data);
  };

  // Updated: Check if schedule is valid (complete and no conflicts)
  const isScheduleValid = () => {
    if (!selectedSchedule) return false;

    // Check if multiple schedule types are configured
    const completedCount = scheduleDataManager.getCompletedScheduleTypesCount();
    if (completedCount > 1) {
      return false;
    }

    // Check if current schedule is complete
    const currentData = scheduleDataManager.getData(selectedSchedule);
    const isCurrentComplete = isScheduleDataComplete(
      selectedSchedule,
      currentData
    );

    // Return true if current schedule is complete and no other schedules are complete
    return isCurrentComplete && completedCount === 1;
  };

  // Handle creative selection
  const handleCreativeSelect = (creativeId: string) => {
    setSelectedCreative((prev) => (prev === creativeId ? null : creativeId));
    setCurrentStep(1);
    setSelectedAnnouncement(null);
    setIsAnnouncementComplete(false);
    setSelectedSchedule("now");
    setIsScheduleComplete(false);
    scheduleDataManager.clearAll();
    setAnnouncementDetails(null);
  };

  // Handle clearing other schedules - NEW FUNCTION
  const handleClearOtherSchedules = (keepType: ScheduleType) => {
    scheduleDataManager.clearOtherScheduleTypes(keepType);
    setSelectedSchedule(keepType);

    // Update completion status based on the kept schedule
    const keptData = scheduleDataManager.getData(keepType);
    const isComplete = isScheduleDataComplete(keepType, keptData);
    setIsScheduleComplete(isComplete);
  };

  // Handle scheduling
  const handleSchedule = () => {
    const selected = creatives.find(
      (creative) => creative.id === selectedCreative
    );

    if (!selected || !selectedAnnouncement || !selectedSchedule) {
      alert("Please complete all required fields before scheduling.");
      return;
    }

    // REMOVED: Creative already scheduled validation
    // Users can now schedule the same creative multiple times

    // Enhanced validation
    const completedTypes = scheduleDataManager.getCompletedScheduleTypes();
    if (completedTypes.length === 0) {
      alert("Please complete the schedule configuration.");
      return;
    }

    if (completedTypes.length > 1) {
      alert(
        `Please select one schedule type to proceed. You have completed: ${completedTypes.join(
          ", "
        )}`
      );
      return;
    }

    const currentScheduleDetails =
      scheduleDataManager.getData(selectedSchedule);
    if (!currentScheduleDetails || !isScheduleValid()) {
      alert("Please complete the schedule configuration.");
      return;
    }

    const creativeTags = generateTags(
      selectedAnnouncement,
      selectedSchedule,
      announcementDetails
    );

    const scheduledCreative: ScheduledCreative = {
      id: `scheduled_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      title: selected.title,
      imageUrl: selected.imageUrl,
      orientation: selected.orientation,
      format: selected.format || "image/jpeg",
      createdOn: new Date().toISOString(),
      dateRange: generateDateRange(selectedSchedule, currentScheduleDetails),
      locations: generateLocationsString(creativeTags),
      tags: creativeTags,
      isPlaying: true,
      scheduleType: selectedSchedule,
      announcement: selectedAnnouncement,
      scheduleDetails: currentScheduleDetails,
    };

    try {
      addScheduledCreative(scheduledCreative);
      handleClosePopup();
      navigate("/scheduled/list");
    } catch (error) {
      console.error("Failed to save scheduled creative:", error);
      alert("Failed to save scheduled creative. Please try again.");
    }
  };

  // Handle popup close
  const handleClosePopup = () => {
    setSelectedCreative(null);
    setCurrentStep(1);
    setSelectedAnnouncement(null);
    setIsAnnouncementComplete(false);
    setSelectedSchedule("now");
    setIsScheduleComplete(false);
    scheduleDataManager.clearAll();
    setAnnouncementDetails(null);
  };

  // Navigation handlers
  const handleNext = () => {
    if (isAnnouncementComplete && currentStep === 1) {
      setCurrentStep(2);
      if (!selectedSchedule) {
        setSelectedSchedule("now");
      }
      setIsScheduleComplete(false);
    }
  };

  const handlePrevious = () => {
    if (currentStep === 2) {
      setCurrentStep(1);
      setIsScheduleComplete(false);
    }
  };

  // Handle announcement selection
  const handleAnnouncementSelect = (
    type: AnnouncementType | null,
    isComplete: boolean = false,
    details?: any
  ) => {
    setSelectedAnnouncement(type);
    setIsAnnouncementComplete(isComplete);
    setAnnouncementDetails(details);
  };

  // Handle schedule selection - Updated
  const handleScheduleSelect = (type: ScheduleType) => {
    setSelectedSchedule(type);
    setIsScheduleComplete(false);

    // Don't clear other schedule types immediately, just switch view
    const currentData = scheduleDataManager.getData(type);

    // Check if this type's data is complete
    if (currentData && isScheduleDataComplete(type, currentData)) {
      setIsScheduleComplete(true);
    }
  };

  // Handle schedule data changes - Updated
  const handleScheduleDataChange = (type: ScheduleType, data: any) => {
    // Always save the data for the current type
    scheduleDataManager.setData(type, data);

    // Update completion status if this is the currently selected schedule type
    if (type === selectedSchedule) {
      const isComplete = isScheduleDataComplete(type, data);
      setIsScheduleComplete(isComplete);
    }
  };

  // Get dimensions based on orientation
  const getDimensions = (orientation: string) => {
    return orientation === "landscape" ? "1920 x 1080" : "1080 x 1920";
  };

  const selectedCreativeData = selectedCreative
    ? creatives.find((creative) => creative.id === selectedCreative)
    : null;

  return (
    <div className="">
      <div className="w-full h-full flex flex-col">
        <div className="flex justify-between items-center p-6">
          <div className="text-sm 2xl:text-base 4xl:text-lg  font-medium text-gray-900">
            Available {filteredCreatives.length} creatives
          </div>
          <div className="flex items-center gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search content"
                className="w-64 pl-10 pr-4 py-2.5 rounded-[8px] border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder:text-sm placeholder:font-medium"
              />
            </div>
            <div className="flex gap-1 text-gray-600">
              <button
                className={`px-3 py-2 rounded-md text-xs font-medium transition-colors ${
                  filter === "all"
                    ? "bg-[#F2FAFD] text-[#0090AD]"
                    : "hover:text-black hover:bg-gray-100"
                }`}
                onClick={() => setFilter("all")}
              >
                <All className="w-5 h-5" />
              </button>
              <button
                className={`px-3 py-2 rounded-md text-xs font-medium transition-colors ${
                  filter === "landscape"
                    ? "bg-[#F2FAFD] text-[#0090AD]"
                    : "hover:text-black hover:bg-gray-100"
                }`}
                onClick={() => setFilter("landscape")}
              >
                <Landscape className="w-5 h-5" />
              </button>
              <button
                className={`px-3 py-2 rounded-md text-xs font-medium transition-colors ${
                  filter === "portrait"
                    ? "bg-[#F2FAFD] text-[#0090AD]"
                    : "hover:text-black hover:bg-gray-100"
                }`}
                onClick={() => setFilter("portrait")}
              >
                <Protrait className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {filteredCreatives.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="text-gray-400 text-center">
                <div className="flex items-center justify-center">
                  <ScheduledCreative className="w-52 h-52 -mb-8" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No creatives found
                </h3>
                <p className="text-gray-500">
                  Try adjusting your search or filters
                </p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6">
              {filteredCreatives.map((creative) => (
                <div
                  key={creative.id}
                  className={`relative ${
                    selectedCreative === creative.id
                      ? "border-[1px] border-[#0BA6C7] rounded-[16px] shadow-[0_0_8px_2px_rgba(181,181,181,0.5)]"
                      : ""
                  }`}
                >
                  <CreativeSelectionCard
                    creative={creative}
                    isSelected={selectedCreative === creative.id}
                    onSelect={() => handleCreativeSelect(creative.id)}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {selectedCreative && selectedCreativeData && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-40">
            <ItemListPopup title="Schedule creative" onClose={handleClosePopup}>
              <div className="flex flex-col h-full">
                <div className="flex-1 overflow-y-auto">
                  <div className="flex flex-col gap-1 2xl:gap-3">
                    <ProgressBar currentStep={currentStep} />

                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-[#323232]">
                        Dimension
                      </span>
                      <span className="text-sm font-normal text-[#323232]">
                        {getDimensions(selectedCreativeData.orientation)}
                      </span>
                    </div>

                    <div className="w-full flex justify-center">
                      <img
                        src={selectedCreativeData.imageUrl}
                        alt={selectedCreativeData.title}
                        className={`h-auto rounded-lg border-4 border-[#A6A6A6] ${
                          selectedCreativeData.orientation === "portrait"
                            ? "w-1/2"
                            : "w-full"
                        }`}
                      />
                    </div>

                    {currentStep === 1 && (
                      <AnnouncementSelector
                        onSelectionChange={handleAnnouncementSelect}
                        selectedType={selectedAnnouncement}
                      />
                    )}

                    {currentStep === 2 && (
                      <ScheduleButtons
                        onScheduleSelect={handleScheduleSelect}
                        selectedSchedule={selectedSchedule}
                        scheduleDataManager={scheduleDataManager}
                        onScheduleDataChange={handleScheduleDataChange}
                        onClearOtherSchedules={handleClearOtherSchedules}
                      />
                    )}
                  </div>
                </div>

                <div className="border-t border-gray-200 my-4"></div>

                <div className="flex gap-3 mt-auto max-w-full">
                  <CustomButton
                    variant="outline"
                    onClick={
                      currentStep === 1 ? handleClosePopup : handlePrevious
                    }
                    className="flex-1 max-w-[calc(50%-6px)]"
                  >
                    {currentStep === 1 ? "Cancel" : "Previous"}
                  </CustomButton>
                  <CustomButton
                    variant="default"
                    isActive={
                      currentStep === 1
                        ? isAnnouncementComplete
                        : isScheduleValid()
                    }
                    onClick={currentStep === 1 ? handleNext : handleSchedule}
                    disabled={
                      currentStep === 1
                        ? !isAnnouncementComplete
                        : !isScheduleValid()
                    }
                    className="flex-1 max-w-[calc(50%-6px)]"
                  >
                    {currentStep === 1 ? "Next" : "Schedule"}
                  </CustomButton>
                </div>
              </div>
            </ItemListPopup>
          </div>
        )}
      </div>
    </div>
  );
};

export default ScheduleCreativePage;
